import React from "react";
import Typography from "../../../common/Typography";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import { normalizePublicImageUrl } from "../../../../utils/imageUrl";

import { ImageHotspotContent } from "../../../../types/newtypes";
import { useImageHotspotHook } from "./hooks/useImageHotspotHook";

interface ImageHotspotPreviewProps {
  content: ImageHotspotContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const ImageHotspotPreview: React.FC<ImageHotspotPreviewProps> = ({
  content,
  onCardStateChange,
}) => {
  const {
    revealedHotspots,
    selectedHotspotIndex,
    handleHotspotClick,
    allHotspotsRevealed,
    remainingHotspots,
    // Image loading state
    imageLoaded,
    imageError,
    handleImageLoad,
    handleImageError,
    // Image dimension tracking
    imageDimensions,
    imageRef,
    containerRef,
  } = useImageHotspotHook({
    content,
    onCardStateChange,
  });

  const imageUrl = normalizePublicImageUrl(content?.image_uri);
  const effectiveImageError = imageError || !imageUrl;

  return (
    <div className="w-full">
      {/* Title */}
      <div className="mb-8">
        <Typography variant="h4" weight="bold" className="mb-4">
          {content?.slide_title}
        </Typography>
        <MarkdownWrapper
          content={content?.intro_text || ""}
          className="!text-base"
        />
      </div>

      {/* Main Content - Responsive Layout */}
      <div className="flex flex-col lg:flex-row lg:grid-cols-2 lg:gap-8">
        {/* Left Section - Image with Hotspots */}
        <div className="mb-6 lg:mb-0 xl:max-w-[45vw] 2xl:min-w-[22vw]">
          <div className="relative" ref={containerRef}>
            {/* Image Container */}
            <div className="relative bg-gray-100 rounded-lg overflow-hidden inline-block w-full">
              {!imageLoaded && !effectiveImageError && (
                <div className="w-full max-w-[600px] h-[400px] flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-gray-200 dark:text-gray-600"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 18"
                  >
                    <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                  </svg>
                </div>
              )}

              {effectiveImageError ? (
                <div className="w-64 h-64 lg:w-80 lg:h-80 flex items-center justify-center bg-gray-200 rounded-lg" data-testid="broken-image">
                  <Typography variant="p" className="text-gray-500">
                    Failed to load image
                  </Typography>
                </div>
              ) : (
                <div className="w-full">
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt={content?.alt_text}
                    className={`w-full h-auto object-contain rounded-lg ${
                      imageLoaded ? "opacity-100" : "opacity-0"
                    } transition-opacity duration-200`}
                    style={{ maxWidth: "100%", maxHeight: "60vh" }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>

            {/* Hotspots Overlay */}
            {imageLoaded && !effectiveImageError && (
              <div className="absolute inset-0 pointer-events-none">
                {content?.hotspots?.map((hotspot, index) => {
                  const isRevealed = revealedHotspots?.has(index);
                  const isSelected = selectedHotspotIndex === index;

                  // Calculate hotspot position relative to actual image dimensions
                  let left, top;

                  if (imageDimensions) {
                    // Position relative to the actual image area
                    const imageX =
                      imageDimensions.offsetX +
                      (hotspot?.x_percentage / 100) * imageDimensions.width;
                    const imageY =
                      imageDimensions.offsetY +
                      (hotspot?.y_percentage / 100) * imageDimensions.height;

                    left = `${imageX}px`;
                    top = `${imageY}px`;
                  } else {
                    // Fallback to container-relative positioning while loading
                    left = `${hotspot?.x_percentage}%`;
                    top = `${hotspot?.y_percentage}%`;
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleHotspotClick(index)}
                      className={`absolute w-10 h-10 md:w-8 md:h-8 transform -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-auto transition-all duration-200 border-2 border-white shadow-lg
                        ${
                    isRevealed
                      ? "bg-[#27AE60]" // Harmonised success green
                      : "bg-amber-400 hover:bg-amber-300"
                    } ${isSelected ? "ring-4 ring-blue-300 ring-opacity-50" : ""}`}
                      style={{
                        left,
                        top,
                      }}
                      aria-label={`Hotspot ${index + 1}: ${hotspot?.reveal_title}`}
                    >
                      <div className="w-full h-full text-sm font-semibold rounded-full flex items-center justify-center">
                        {index + 1}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Revealed Content */}
        <div className="lg:pl-4 flex-1 2xl:max-w-[55vw]">
          {selectedHotspotIndex !== null ? (
            // Show content for the selected hotspot
            (() => {
              const selectedHotspot = content?.hotspots[selectedHotspotIndex];

              return (
                <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-6">
                  <Typography
                    variant="h5"
                    weight="bold"
                    className="mb-3 max-sm:!text-lg text-green-800"
                  >
                    {selectedHotspot?.reveal_title}
                  </Typography>
                  <div className="!text-base text-gray-800 max-h-[40vh] overflow-y-auto text-left">
                    {selectedHotspot?.reveal_text}
                  </div>
                </div>
              );
            })()
          ) : (
            // Show placeholder when no hotspot is selected
            <div className="bg-background rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-center py-8">
                <Typography variant="p" className="!text-base">
                  Click on a hotspot to reveal its information
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section - Instructions */}
      <div className="mt-6">
        <Typography variant="small" className="!text-sm">
          Click on the colored dots to explore different parts of the diagram
        </Typography>
      </div>

      {!allHotspotsRevealed ? (
        <div className="mt-2 mb-4 p-2 text-xs bg-blue-50 border border-blue-200 rounded-lg inline-block">
          <Typography variant="small">
            Click on all hotspots to proceed for next. {remainingHotspots}{" "}
            hotspot
            {remainingHotspots !== 1 ? "s" : ""} remaining...
          </Typography>
        </div>
      ) : (
        <div className="mt-2 mb-4 p-2 text-xs bg-green-50 border border-green-200 rounded-lg inline-block">
          <Typography variant="small" className="text-green-800 text-center">
            All hotspots have been revealed! You can now proceed.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default ImageHotspotPreview;
